import React, { useState } from 'react';
import { Agent } from '../../../types';
import { Button } from '../../common';
import { Modal } from '../../common/Modal';
import { useAdminTranslation } from '../../../modules/i18n';
import { ClipboardDocumentIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';

interface AgentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
}

const AgentDetailModal: React.FC<AgentDetailModalProps> = ({ isOpen, onClose, agent }) => {
  const { t } = useAdminTranslation();
  const [showAgentId, setShowAgentId] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  if (!isOpen) return null;

  const handleCopyId = async () => {
    await navigator.clipboard.writeText(agent.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  // KG Status Badge Component
  const KGStatusBadge = () => {
    const kg = agent.knowledgeGraph;
    if (!kg) {
      return <span className='text-sm text-gray-400 dark:text-dark-text-secondary'>{t('content.agents.kgStatus.noKG')}</span>;
    }

    const statusConfig = {
      COMPLETED: {
        label: t('content.agents.kgStatus.ready'),
        className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      },
      PROCESSING: {
        label: t('content.agents.kgStatus.building'),
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      },
      PENDING: {
        label: t('content.agents.kgStatus.pending'),
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      },
      ERROR: {
        label: t('content.agents.kgStatus.error'),
        className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      }
    };

    const config = statusConfig[kg.status as keyof typeof statusConfig] || statusConfig.PENDING;

    return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.className}`}>{config.label}</span>;
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return {
          text: t('content.agents.status.active'),
          className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
        };
      case 'PROCESSING':
        return {
          text: t('content.agents.status.processing'),
          className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
        };
      case 'PENDING':
        return {
          text: t('content.agents.status.pending'),
          className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
        };
      case 'ERROR':
        return {
          text: t('content.agents.status.error'),
          className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
        };
      default:
        return {
          text: t('content.agents.status.unknown'),
          className: 'bg-gray-100 text-gray-800 dark:bg-dark-background/30 dark:text-dark-text-secondary'
        };
    }
  };

  const statusInfo = getStatusDisplay(agent.status);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('content.agents.detail.title')}
      size='md'
      height='lg'
      footer={
        <Button onClick={onClose} variant='outline'>
          {t('content.agents.detail.closeButton')}
        </Button>
      }
    >
      <div className='space-y-6'>
        <div className='space-y-4'>
          <div className='flex items-center space-x-3'>
            <div className='text-3xl'>🤖</div>
            <div>
              <h4 className='font-medium text-gray-900 dark:text-dark-text'>{agent.name}</h4>
              <p className='text-sm text-gray-500 dark:text-dark-text-secondary'>{t('content.agents.detail.aiAgent')}</p>
            </div>
          </div>

          {/* Agent ID - toggle to reveal */}
          <div>
            {!showAgentId ? (
              <button
                onClick={() => setShowAgentId(true)}
                className='inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 dark:border-border-dark dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
              >
                <svg className='h-3.5 w-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' />
                </svg>
                {t('content.agents.detail.fields.showAgentId')}
              </button>
            ) : (
              <div className='flex items-center gap-2'>
                <code className='flex-1 rounded-md bg-gray-100 px-3 py-1.5 text-xs text-gray-700 dark:bg-dark-background-tertiary dark:text-dark-text-secondary'>{agent.id}</code>
                <button
                  onClick={handleCopyId}
                  className='rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-background-hover dark:hover:text-dark-text'
                  title={t('content.agents.detail.fields.agentIdCopied')}
                >
                  {copiedId ? (
                    <ClipboardDocumentCheckIcon className='h-4 w-4 text-green-500' />
                  ) : (
                    <ClipboardDocumentIcon className='h-4 w-4' />
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Knowledge Graph Information */}
          {agent.knowledgeGraph && (
            <div className='rounded-lg border-2 border-primary/20 bg-primary/5 p-4 dark:border-primary/40 dark:bg-primary/10 dark:text-dark-text-secondary'>
              <h4 className='mb-3 flex items-center gap-2 text-sm font-semibold text-primary dark:text-dark-text-secondary'>
                <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
                  />
                </svg>
                {t('content.agents.detail.fields.kgInfo')}
              </h4>
              <dl className='space-y-2 text-sm'>
                <div className='flex justify-between'>
                  <dt className='font-medium text-primary dark:text-dark-text-secondary'>{t('content.agents.detail.fields.kgStatus')}:</dt>
                  <dd>
                    <KGStatusBadge />
                  </dd>
                </div>
                <div className='flex justify-between'>
                  <dt className='font-medium text-primary dark:text-dark-text-secondary'>{t('content.agents.detail.fields.namespace')}:</dt>
                  <dd className='text-primary dark:text-dark-text-secondary'>{agent.knowledgeGraph.namespace || 'N/A'}</dd>
                </div>
                {agent.knowledgeGraph.version && (
                  <div className='flex justify-between'>
                    <dt className='font-medium text-primary dark:text-dark-text-secondary'>{t('content.agents.detail.fields.version')}:</dt>
                    <dd className='text-primary dark:text-dark-text-secondary'>{agent.knowledgeGraph.version}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <div>
              <span className='font-medium text-gray-700 dark:text-dark-text-secondary'>{t('content.agents.detail.fields.agentType')}</span>
              <div className='mt-1'>
                {agent.agentType === 'DIRECT_QA' ? (
                  <span className='inline-flex rounded-md bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300'>
                    {t('content.agents.agentType.directQa')}
                  </span>
                ) : (
                  <span className='inline-flex rounded-md bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'>
                    {t('content.agents.agentType.kgRag')}
                  </span>
                )}
              </div>
            </div>
            <div>
              <span className='font-medium text-gray-700 dark:text-dark-text-secondary'>{t('content.agents.detail.fields.language')}</span>
              <p className='text-gray-900 dark:text-dark-text'>
                {agent.language?.name || agent.language?.code || t('content.agents.detail.fields.unknown')}
              </p>
            </div>
          </div>

          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <div>
              <span className='font-medium text-gray-700 dark:text-dark-text-secondary'>{t('content.agents.detail.fields.status')}</span>
              <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusInfo.className}`}>{statusInfo.text}</span>
            </div>
          </div>

          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <div>
              <span className='font-medium text-gray-700 dark:text-dark-text-secondary'>{t('content.agents.detail.fields.created')}</span>
              <p className='text-gray-900 dark:text-dark-text'>{new Date(agent.createdAt).toLocaleString('vi-VN')}</p>
            </div>
            <div>
              <span className='font-medium text-gray-700 dark:text-dark-text-secondary'>{t('content.agents.detail.fields.updated')}</span>
              <p className='text-gray-900 dark:text-dark-text'>{new Date(agent.updatedAt).toLocaleString('vi-VN')}</p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AgentDetailModal;
